import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/client';
import { BookOpen, FileText, ListChecks, Search, StickyNote, X } from 'lucide-react';
import { DOCUMENTS_QUERY, SEARCH_DOCUMENTS_QUERY } from '../graphql/queries';
import { FOCUS_RING_CLASSES, trapTabKey } from '../lib/focusTrap';
import { useScrollLock } from '../hooks/useScrollLock';
import { displayTitle, fileStem, typeFromPath } from '../lib/docView';

interface DocumentSummary {
  path: string;
  title: string;
  type: string | null;
  updatedAt: string;
  treeLabel: string;
  spaceId: string | null;
}

interface DocumentsData {
  documents: DocumentSummary[];
}

interface SearchHit {
  path: string;
  title: string;
  type: string | null;
  treeLabel: string;
  spaceId: string | null;
  snippet: string | null;
}

interface SearchData {
  searchDocuments: SearchHit[];
}

/** Merged result row: a client (title) hit, optionally enriched by a server snippet. */
interface ResultRow {
  path: string;
  title: string;
  type: string | null;
  treeLabel: string;
  spaceId: string | null;
  snippet: string | null;
}

const GROUP_ORDER = ['journal', 'list', 'note', 'other'] as const;
const GROUP_LABELS: Record<(typeof GROUP_ORDER)[number], string> = {
  journal: 'Days',
  list: 'Lists',
  note: 'Notes',
  other: 'Documents',
};

function rowIcon(type: string | null) {
  switch (type) {
    case 'journal':
      return BookOpen;
    case 'list':
      return ListChecks;
    case 'note':
      return StickyNote;
    default:
      return FileText;
  }
}

/** Debounce delay before the body-text search round-trip fires. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * The document search overlay (design review E1): an instant, as-you-type
 * "find" layer — title/stem/path matches from the already-cached tree render
 * immediately, and a debounced `searchDocuments` round-trip adds body-text
 * hits with snippets. Grouped by type, each hit deep-links to its document.
 * Ask (the agent) stays the *question* layer; this is the *find* layer.
 */
export function SearchOverlay({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  useScrollLock(true);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const { data: docsData } = useQuery<DocumentsData>(DOCUMENTS_QUERY, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  const [runSearch, { data: searchData }] = useLazyQuery<SearchData>(SEARCH_DOCUMENTS_QUERY, {
    errorPolicy: 'ignore',
  });

  // Debounced body-text search; title matches below stay instant regardless.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    const timer = setTimeout(() => {
      void runSearch({ variables: { query: trimmed } });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  const results = useMemo<ResultRow[]>(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];

    const rows = new Map<string, ResultRow>();
    for (const doc of docsData?.documents ?? []) {
      const title = displayTitle(doc.path, doc.title);
      const haystack = `${title}\n${doc.title}\n${fileStem(doc.path)}\n${doc.path}`.toLowerCase();
      if (!haystack.includes(needle)) continue;
      rows.set(doc.path, {
        path: doc.path,
        title,
        type: doc.type ?? typeFromPath(doc.path),
        treeLabel: doc.treeLabel,
        spaceId: doc.spaceId,
        snippet: null,
      });
    }

    // Server hits: enrich title hits with snippets; append body-only hits.
    // Only merge when the response matches what's currently typed.
    if (query.trim().length >= 2) {
      for (const hit of searchData?.searchDocuments ?? []) {
        const existing = rows.get(hit.path);
        if (existing) {
          if (hit.snippet && !existing.snippet) existing.snippet = hit.snippet;
        } else {
          rows.set(hit.path, {
            path: hit.path,
            title: displayTitle(hit.path, hit.title),
            type: hit.type ?? typeFromPath(hit.path),
            treeLabel: hit.treeLabel,
            spaceId: hit.spaceId,
            snippet: hit.snippet,
          });
        }
      }
    }
    return [...rows.values()];
  }, [query, docsData?.documents, searchData?.searchDocuments]);

  const grouped = useMemo(() => {
    const groups = new Map<(typeof GROUP_ORDER)[number], ResultRow[]>();
    for (const row of results) {
      const key = (
        row.type === 'journal' || row.type === 'list' || row.type === 'note' ? row.type : 'other'
      ) as (typeof GROUP_ORDER)[number];
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row);
    }
    return GROUP_ORDER.filter((key) => groups.has(key)).map((key) => ({
      key,
      label: GROUP_LABELS[key],
      rows: groups.get(key)!,
    }));
  }, [results]);

  const openResult = (path: string) => {
    navigate(`/docs/${path}`);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(e, panelRef.current);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (results.length > 0) openResult(results[0].path);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 backdrop-blur-sm p-0 sm:p-4 sm:pt-[10vh]"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="glass-panel bg-[var(--surface)] w-full sm:max-w-xl rounded-b-3xl sm:rounded-3xl shadow-warm animate-sheet-up max-h-[min(80dvh,100%)] flex flex-col safe-top"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label="Search your documents"
        tabIndex={-1}
      >
        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-2 px-4 py-3 border-b border-[var(--card-border)]"
        >
          <Search className="w-5 h-5 text-[var(--text-tertiary)] shrink-0" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your documents…"
            aria-label="Search your documents"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            className="!border-0 !shadow-none !bg-transparent flex-1 min-w-0 px-0"
          />
          <button
            type="button"
            onClick={onClose}
            className={`p-2 hover:bg-[color-mix(in_oklab,var(--text-primary)_7%,transparent)] rounded-full transition-colors shrink-0 ${FOCUS_RING_CLASSES}`}
            aria-label="Close search"
          >
            <X className="w-5 h-5 text-[var(--text-tertiary)]" aria-hidden />
          </button>
        </form>

        <div className="flex-1 overflow-y-auto overscroll-contain p-2" data-testid="search-results">
          {query.trim().length === 0 ? (
            <p className="px-3 py-6 text-sm text-[var(--text-tertiary)] text-center">
              Type to find days, lists, and notes by name or content.
            </p>
          ) : results.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <p className="text-sm text-[var(--text-secondary)]">Nothing matched that.</p>
              <p className="mt-1 text-sm text-[var(--text-tertiary)]">
                For questions about your life, try{' '}
                <Link
                  to="/alma"
                  onClick={onClose}
                  className={`font-medium text-[var(--accent-ink)] ${FOCUS_RING_CLASSES}`}
                >
                  asking me
                </Link>
                .
              </p>
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.key} className="mb-2">
                <p className="px-3 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">
                  {group.label}
                </p>
                <ul>
                  {group.rows.map((row) => {
                    const Icon = rowIcon(row.type);
                    return (
                      <li key={row.path}>
                        <button
                          type="button"
                          onClick={() => openResult(row.path)}
                          className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-colors ${FOCUS_RING_CLASSES}`}
                        >
                          <span
                            className="w-8 h-8 rounded-xl bg-[color-mix(in_oklab,var(--marine-400)_12%,transparent)] text-[var(--accent-ink)] flex items-center justify-center shrink-0"
                            aria-hidden
                          >
                            <Icon className="w-4 h-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-[var(--text-primary)]">
                              {row.title}
                            </span>
                            {row.snippet && (
                              <span className="block truncate text-sm text-[var(--text-tertiary)]">
                                {row.snippet}
                              </span>
                            )}
                          </span>
                          {row.spaceId && (
                            <span className="text-xs font-medium text-[var(--accent-ink)] bg-[color-mix(in_oklab,var(--marine-400)_12%,transparent)] px-2 py-1 rounded-full shrink-0">
                              {row.treeLabel}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
